<template>
  <div>
    <breadcrumbs home="Audit log"/>

    <purtrak-table name="audit" :defaultColumns="defaultColumns" :headers="headers" ref="table"
                   listEndPoint="auditLogEntries"
                   fields="id,time,userId,userName,event,entityId,entityType,request"
                   :listFilters="listFilters" title="Audit log"
                   :print-info="{pageSize:'landscape'}"
                   @dblclick="json"
                   download-endpoint="auditLogExportCSV">
      <template v-slot:filter>
        <filter-menu include-reset @reset="filters.from=null;filters.to=null;filters.search=''">
          <v-list-item>
            <filter-search label="Event" v-model="filters.search"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" ref="dateRange" option="All" allow-all/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Search: {{filters.search}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.userId!=null" close small class="ml-1" @click:close="filters.userId=null">For user</v-chip>
          <v-chip v-show="filters.entityId!=null" close small class="ml-1" @click:close="filters.entityId=null">For target</v-chip>
        </template>
      </template>

      <template v-slot:item.time="{value}">
        {{value | formatDateTime}}
      </template>
      <template v-slot:item.userName="{item,printing}">
        <template v-if="printing">{{item.userName}}</template>
        <v-hover v-else v-slot="{hover}">
          <a @click.stop="user(item)"
              :class="{'text-decoration-underline':hover}">{{item.userName}}</a>
        </v-hover>
      </template>
      <template v-slot:item.target="{item,printing}">
        <template v-if="printing">{{target(item)}}</template>
        <v-menu v-else bottom nudge-bottom="20">
          <template v-slot:activator="{on}">
            <v-hover v-slot="{hover}">
              <a v-on="on" :class="{'text-decoration-underline':hover}">{{target(item)}}</a>
            </v-hover>
          </template>
          <v-list dense class="text-caption">
            <v-list-item @click.stop="history(item)">Filter by target</v-list-item>
            <v-list-item @click.stop="details(item)">View target</v-list-item>
          </v-list>
        </v-menu>
      </template>
    </purtrak-table>

    <v-dialog v-model="showRequest" persistent max-width="800">
    <v-card>
      <v-card-title>{{request.event}}</v-card-title>
      <v-divider/>
        <v-textarea v-model="request.request" rows="10" outlined readonly class="mx-6 mt-6"/>
      <v-divider/>
      <v-card-actions class="pr-6">
        <v-spacer></v-spacer>
        <v-btn plain color="primary" @click="showRequest=false">Done</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  </div>
</template>

<script>
import viewTransaction from '@/mixins/viewTransaction'

export default {
  name: 'ToolsAudit',
  mixins: [viewTransaction],
  data() {
    return {
      filters: {
        from: null,
        to: null,
        entityId: null,
        userId: null,
        search: ''
      },
      dateDisplay: '',
      showRequest: false,
      request: {},
      defaultColumns: [{ value: 'time', visible:true},
                       { value: 'userName', visible:true},
                       { value: 'event', visible:true},
                       { value: 'target', visible:true}],
      headers: [{ text: 'TIME', value: 'time', width:'20%',sortable:true},
                { text: 'USER', value: 'userName', width:'20%',sortable:false},
                { text: 'EVENT', value: 'event', width:'40%',sortable:true},
                { text: 'TARGET', value: 'target', width:'20%',sortable:false}]
    }
  },
  computed: {
    listFilters() {
      const filters = [];
      if (this.filters.from)
        filters.push({field:'time', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'time', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.entityId)
        filters.push({field:'entityId', comparator:'eq', value:this.filters.entityId});
      if (this.filters.userId)
        filters.push({field:'userId', comparator:'eq', value:this.filters.userId});
      if (this.filters.search)
        filters.push({field:'event', comparator:'contains', value:this.filters.search});
      return filters;
    }
  },
  methods: {
    user(log) {
      if (this.filters.userId !== log.userId) {
        this.filters.userId = log.userId;
      }
    },
    history(log) {
      if (this.filters.entityId !== log.entityId) {
        this.filters.entityId = log.entityId;
      }
    },
    details(log) {
      this.viewTransaction({refType:log.entityType,refId:log.entityId})
    },
    async json(log) {
      if (this.$store.state.user.admin) {
        this.loading = true;
        const results = await this.$apiCall(`query ToolsAudit($id: Int!) {
                                                  auditEntry(id:$id) {
                                                    event,request
                                                  }
                                                }`,
                                            {id:log.id});
        this.loading = false;
        if (!results.error) {
          this.request = results.data.auditEntry;
          this.request.request = JSON.stringify(JSON.parse(this.request.request), null, 2);
          this.showRequest = true;
        }
      }
    },
    target(log) {
      const targets = [
        {type:'BulkContainer',target:'Bulk container'},
        {type:'Vendor',target:'Supplier'},
        {type:'FermenterTransactionReduceInventory',target:'Reduce fermenter inventory'},
        {type:'FermenterTransactionTransfer',target:'Transfer to fermenter'},

        {type:'ItemTransactionAdjustInventory',target:'Adjust item inventory'},
        {type:'ItemTransactionTransferSite',target:'Move to site'},
        {type:'ItemTransactionTransferLot',target:'Reassign lot'},

        {type:'MashBill',target:'Mash bill'},
        {type:'ProductTransactionReduceInventory',target:'Reduce product inventory'},
        {type:'ProductTransactionAdjustInventory',target:'Adjust product inventory'},
        {type:'ProductTransactionTransferSite',target:'Move to site'},
        {type:'ProductTransactionMoveToDutyPaid',target:'Move to duty paid'},
        {type:'ProductTransactionMoveToInBond',target:'Move to in-bond'},
        {type:'ProductTransactionReturnToTank',target:'Return to tank'},
        {type:'ProductTransactionConvert',target:'Convert product'},
        {type:'ProductTransactionTransferLot',target:'Reassign lot'},

        {type:'ExciseVesselTransactionReduceInventory',target:'Reduce inventory'},
        {type:'ExciseVesselTransactionFortifyWine',target:'Fortify wine'},
        {type:'ExciseVesselTransactionDenature',target:'Denature'},
        {type:'ExciseVesselTransactionAdjustInventory',target:'Adjust inventory'},
        {type:'ExciseVesselTransactionTransferToTank',target:'Move to tank'},
        {type:'ExciseVesselTransactionTransferToBulkContainer',target:'Move to bulk container'},
        {type:'ExciseVesselTransactionTransferToUserLicense',target:'Transfer to user license'},
        {type:'ExciseVesselTransactionTransferToSpiritsLicense',target:'Transfer to spirits license'},
        {type:'ExciseVesselTransactionTransferSite',target:'Move to site'},

        {type:'TankTransactionProof',target:'Proof'},
        {type:'TankTransactionFillCask',target:'Fill casks'},

        {type:'SalesOrder',target:'Sales order'},
        {type:'OtherEquipment',target:'Other equipment'}

      ];
      return targets.find(target => target.type === log.entityType)?.target ?? log.entityType;
    }
  }
}
</script>